/**
 * Color math ported from Nanome.Domain/Scenes/Components/Colors/HexColor.cs and Palette.cs so that
 * interpolated palettes (B-factor, atom serial) and indexed palettes (chain, model) match app.nanome.ai exactly.
 */

export type Hex = number; // 0xRRGGBB

export const hex = (r: number, g: number, b: number): Hex => ((r & 255) << 16) | ((g & 255) << 8) | (b & 255);
export const hexR = (c: Hex) => (c >> 16) & 255;
export const hexG = (c: Hex) => (c >> 8) & 255;
export const hexB = (c: Hex) => c & 255;
export const hexToCss = (c: Hex) => '#' + c.toString(16).padStart(6, '0').toUpperCase();
/** Workspace API wire form: 'RRGGBB' without '#'. */
export const hexToApi = (c: Hex) => c.toString(16).padStart(6, '0').toUpperCase();
export const hexFromApi = (s: string): Hex => parseInt(s.replace(/^#|^0x/i, ''), 16);
/** Linear-free 0..1 floats, as the renderer uploads them. */
export const hexToFloat = (c: Hex): [number, number, number] => [hexR(c) / 255, hexG(c) / 255, hexB(c) / 255];

/** HexColor.ToHSL (HexColor.cs:52-73). Single-precision in C#; double here, which only moves rounding at the byte boundary. */
export function toHSL(c: Hex): [number, number, number] {
  const r = hexR(c) / 255, g = hexG(c) / 255, b = hexB(c) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  const d = max - min;
  if (d > 0) {
    s = l < 0.5 ? d / (max + min) : d / (2 - max - min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return [h, s, l];
}

function hueToRGB(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

/** HexColor.HSLToRGB (HexColor.cs:75-93). Note the C# cast truncates (byte)(x*255); we truncate too. */
export function hslToHex(h: number, s: number, l: number): Hex {
  if (s === 0) {
    const v = Math.trunc(l * 255);
    return hex(v, v, v);
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return hex(Math.trunc(hueToRGB(p, q, h + 1 / 3) * 255), Math.trunc(hueToRGB(p, q, h) * 255), Math.trunc(hueToRGB(p, q, h - 1 / 3) * 255));
}

/** HexColor.Interpolate (HexColor.cs:26-51): HSL, hue along the shortest path. */
export function interpolate(c1: Hex, c2: Hex, t: number): Hex {
  t = Math.min(1, Math.max(0, t));
  let [h1, s1, l1] = toHSL(c1);
  let [h2, s2, l2] = toHSL(c2);
  const dh = h2 - h1;
  if (Math.abs(dh) > 0.5) {
    if (dh > 0) h1 += 1; else h2 += 1;
  }
  let h = (h1 + t * (h2 - h1)) % 1;
  if (h < 0) h += 1;
  return hslToHex(h, s1 + t * (s2 - s1), l1 + t * (l2 - l1));
}

/** Palette[int] (Palette.cs:18): colors[|i| % n]. */
export function paletteIndexed(colors: readonly Hex[], i: number): Hex {
  return colors[Math.abs(i) % colors.length]!;
}

/** Palette[float] (Palette.cs:20-34): clamp, linear between neighbours, HSL interpolation. */
export function paletteInterpolated(colors: readonly Hex[], t: number): Hex {
  t = Math.min(1, Math.max(0, t));
  if (Math.abs(t - 1) < Number.EPSILON) return colors[colors.length - 1]!;
  const index = t * (colors.length - 1);
  const lower = Math.trunc(index);
  return interpolate(colors[lower]!, colors[lower + 1]!, index - lower);
}

/**
 * Chain-instance palette key (RenderingViewModel.Coloring.cs:186-194, 240-256):
 * (entrySerial-1)*397 + chainIndex, where chainIndex is the order of first appearance of the chain name in the model.
 * The palette then takes |key| % 11 (UniqueLowSaturated). ModelInstance uses modelSerial in place of chainIndex.
 */
export const chainInstanceKey = (entrySerial: number, chainIndex: number) => ((entrySerial - 1) * 397 + chainIndex) | 0;
export const modelInstanceKey = (entrySerial: number, modelSerial: number) => ((entrySerial - 1) * 397 + modelSerial) | 0;

/** Named constants used by presets (HexColor.cs:118-140). */
export const NAMED = {
  Red: 0xff0000, Blue: 0x0000ff, Green: 0x00ff00, White: 0xffffff, Magenta: 0xff00ff, Yellow: 0xffff00,
  LightRed: 0xcc003d, Orange: 0xff6600, DarkBlue: 0x6443ff, LightBlue: 0x0080ff, LightGreen: 0x3ae2aa,
  Gray: 0x808080, Purple: 0xa020f0, Turquoise: 0x00ced1,
} as const;

/** HydrogenBonding coloring (HydrogenBondingColors.cs:26-42). Priority: acceptor > donor > hydrophobic > aromatic. */
export const HBOND_COLORS = { default: 0x7d7d7d, donor: 0x0000ff, acceptor: 0xff0000, hydrophobic: 0x00ff00, aromatic: 0xffffff } as const;
